import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import BaseIcon from "../ui/BaseIcon.jsx";
import UserAvatar from "../ui/UserAvatar.jsx";
import BaseDivider from "../ui/BaseDivider.jsx";
import SessionTimer from "../SessionTimer.jsx";
import { useLayout } from "../../context/LayoutContext.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import menuNavBarItems from "../../menuNavBar.js";
import menuItems from "../../menuAside.js";
import { hasRole } from "../../roles.js";
import {
  mdiWeatherNight,
  mdiWeatherSunny,
  mdiRobot,
  mdiChevronDown,
} from "@mdi/js";

function NavItem({ item }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const linkBase = "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap";
  const activeClass = "bg-black/10 dark:bg-white/15 text-slate-900 dark:text-white";
  const inactiveClass = "text-slate-600 dark:text-slate-300 hover:bg-black/[0.07] dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white";

  if (item.children) {
    const isAnyChildActive = item.children.some((c) => location.pathname.startsWith(c.to));
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={[linkBase, isAnyChildActive ? activeClass : inactiveClass].join(" ")}
        >
          {item.icon && <BaseIcon path={item.icon} size={16} className="flex-none opacity-80" />}
          {item.label}
          <BaseIcon
            path={mdiChevronDown}
            size={14}
            className={["opacity-60 transition-transform duration-150", open ? "rotate-180" : ""].join(" ")}
          />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-52 rounded-xl border border-black/[0.08] dark:border-white/10 bg-[#f0f2f5] dark:bg-[#111b21] shadow-xl py-1.5 z-50">
            {item.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  ["flex items-center gap-2.5 px-4 py-2 text-sm transition-colors",
                    isActive ? "text-slate-900 dark:text-white bg-black/[0.08] dark:bg-white/10" : "text-slate-600 dark:text-slate-300 hover:bg-black/[0.06] dark:hover:bg-white/[0.08] hover:text-slate-900 dark:hover:text-white"].join(" ")
                }
              >
                {child.icon && <BaseIcon path={child.icon} size={15} className="flex-none opacity-70" />}
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => [linkBase, isActive ? activeClass : inactiveClass].join(" ")}
    >
      {item.icon && <BaseIcon path={item.icon} size={16} className="flex-none opacity-80" />}
      {item.label}
    </NavLink>
  );
}

export default function NavBar() {
  const { isDarkMode, toggleDark } = useLayout();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropRef = useRef(null);

  const handleLogout = async () => {
    try {
      await axios.post("/auth/logout");
    } catch (error) {
      // Igualmente seguimos: el token local se elimina abajo.
    }
    localStorage.removeItem("agent_token");
    setUser(null);
    navigate("/login");
  };

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const visibleItems = menuItems.filter(
    (item) => !item.roles || hasRole(user, item.roles)
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-20 h-14 bg-[#f0f2f5] dark:bg-[#111b21] border-b border-black/[0.08] dark:border-white/[0.06] flex items-center px-4 gap-2">
      {/* Brand */}
      <div className="flex items-center gap-2 flex-none mr-2">
        <div className="w-7 h-7 rounded-lg bg-flux-green flex items-center justify-center flex-none">
          <BaseIcon path={mdiRobot} size={16} className="text-white" />
        </div>
        <span className="font-bold text-slate-800 dark:text-white text-xs leading-tight tracking-wide hidden md:block">
          Centro de Agentes Flux
        </span>
      </div>

      {/* Nav links — scrollable on small screens */}
      <div className="flex items-center gap-0.5 flex-1 min-w-0 overflow-x-auto">
        {visibleItems.map((item, i) => (
          <NavItem key={item.to || item.label || i} item={item} />
        ))}
      </div>

      {/* Right side */}
      <div className="flex items-center gap-1 flex-none">
        <SessionTimer />

        <button
          type="button"
          onClick={toggleDark}
          className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-black/[0.07] dark:hover:bg-white/10 transition-colors"
          aria-label="Cambiar tema"
        >
          <BaseIcon path={isDarkMode ? mdiWeatherSunny : mdiWeatherNight} size={18} />
        </button>

        <div className="relative" ref={dropRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-black/[0.07] dark:hover:bg-white/10 transition-colors"
          >
            <UserAvatar username={user?.name || user?.email} size="sm" />
            <span className="hidden sm:block text-sm text-slate-700 dark:text-slate-300 max-w-[120px] truncate">
              {user?.name || user?.email}
            </span>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-1.5 w-48 rounded-xl border border-black/[0.08] dark:border-white/10 bg-[#f0f2f5] dark:bg-[#111b21] shadow-lg py-1.5 z-50">
              {menuNavBarItems.map((item, i) => {
                if (item.isDivider) return <BaseDivider key={i} />;
                if (item.isDarkModeToggle) return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { toggleDark(); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-black/[0.06] dark:hover:bg-white/10 transition-colors"
                  >
                    <BaseIcon path={isDarkMode ? mdiWeatherSunny : mdiWeatherNight} size={16} />
                    {isDarkMode ? "Modo claro" : "Modo oscuro"}
                  </button>
                );
                if (item.isLogout) return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { handleLogout(); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-500 dark:text-red-400 hover:bg-black/[0.06] dark:hover:bg-white/10 transition-colors"
                  >
                    <BaseIcon path={item.icon} size={16} />
                    {item.label}
                  </button>
                );
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { navigate(item.to); setDropdownOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-black/[0.06] dark:hover:bg-white/10 transition-colors"
                  >
                    <BaseIcon path={item.icon} size={16} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
